import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { buttonBaseClasses, buttonClasses, SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { deleteV1CompaniesCompanyIdFileAttachmentsId } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { cloneElement, useContext, useMemo, useRef } from 'react';

import Menu, { MenuHandler } from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { FileAttachment } from '~hooks/useFileAttachment/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { downloadFile } from '~utils/utilFunctions';

import AllFilesQueryContext from '../../JobsTable/ExpandedBlock/AllFilesQueryContext';

interface ActionsColumnProps {
  file: FileAttachment;
  sx?: SxProps<Theme>;
}

export default function ActionsColumn({ file, sx }: ActionsColumnProps) {
  const allFilesQuery = useContext(AllFilesQueryContext);
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const menuHandler = useRef<MenuHandler>(null);

  const dropdownMenuActions = useMemo(() => {
    const canDeleteFile = file.deletable;

    const actions = [];

    if (canDeleteFile) {
      actions.push({
        icon: <DeleteOutlined />,
        label: t('actions.delete'),
        onClick: () => {
          deleteV1CompaniesCompanyIdFileAttachmentsId({
            path: { 'company-id': companyId, id: file.id },
          })
            .then(() => {
              allFilesQuery.refresh();
            })
            .catch((error) => {
              connection.handleRequestError(
                error,
                t('error_messages.file_attachment.failed_to_delete'),
              );
            });
        },
      });
    }

    return actions;
  }, [file, companyId]);

  return (
    <Box sx={sx}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}
      >
        <SmallButton
          color="brandV2TreadGray3"
          variant="text"
          startIcon={<FileDownloadOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            downloadFile(file.fileName, file.fileUrl, true);
          }}
          sx={(theme) => ({
            [`&.${buttonBaseClasses.root}`]: {
              border: 'solid 1px transparent',
              borderRadius: '50%',
              height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
              minWidth: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
              p: 0,
              width: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
              '&:hover': { borderColor: theme.brandV2.colors.treadGray3 },
              [`.${buttonClasses.startIcon}`]: { mx: 0 },
            },
          })}
        />

        {Boolean(dropdownMenuActions.length) && (
          <Menu
            ref={menuHandler}
            menuTrigger={
              <SmallButton
                color="brandV2TreadGray3"
                variant="text"
                startIcon={<MoreHoriz />}
                sx={(theme) => ({
                  [`&.${buttonBaseClasses.root}`]: {
                    border: 'solid 1px transparent',
                    borderRadius: '50%',
                    height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                    minWidth: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                    p: 0,
                    width: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                    '&:hover': { borderColor: theme.brandV2.colors.treadGray3 },
                    [`.${buttonClasses.startIcon}`]: { mx: 0 },
                  },
                })}
              />
            }
          >
            {dropdownMenuActions.map((action) => (
              <MenuItem
                key={action.label}
                onClick={(event) => {
                  event.stopPropagation();
                  action.onClick();
                  menuHandler.current?.onClose?.();
                }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    width: '100%',
                    gap: 1,
                  }}
                >
                  {cloneElement(action.icon, {
                    sx: { ...action.icon.props.sx, fontSize: '16px' },
                  })}

                  <Typography
                    sx={(theme) => ({
                      color: theme.brandV2.colors.treadBlack,
                      fontSize: '12px',
                    })}
                  >
                    {action.label}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>
        )}
      </Box>
    </Box>
  );
}
